'use client'

import { useEffect, useId, useMemo } from 'react'
import { IconX } from '@tabler/icons-react'
import Image from 'next/image'

import { urlFor } from 'lib/sanity/image'
import { getCMSCtaLinkParams } from 'lib/sanity/links'
import RichText from 'lib/sanity/richText'
import { useOverlayStack } from 'shared/contexts/OverlayStackContext'
import Button from 'shared/ui/Button'
import { ButtonVariants } from 'shared/ui/Button/Button.enums'
import LinkButton from 'shared/ui/Button/LinkButton'
import { ClientOnlyPortal } from 'shared/ui/primitives/ClientOnlyPortal'
import { KEYBOARD_KEYS } from 'shared/ui/types'
import { addTestId } from 'shared/utils/tests'

import { useMarketingPopover } from '../hooks/useMarketingPopover'

import styles from './MarketingPopover.module.scss'

const OVERLAY_ID = 'marketing-popover'

const MarketingPopover = () => {
  const { isOpen, data, close } = useMarketingPopover()

  const { register, unregister, isTop, getZIndex } = useOverlayStack()
  const internalId = useId()
  const overlayStackId = `${OVERLAY_ID}-${internalId}`
  const zIndex = useMemo(
    () => getZIndex(overlayStackId),
    [getZIndex, overlayStackId]
  )

  // Register in overlay stank on open, unregister on unmount
  useEffect(() => {
    if (!isOpen) return

    register({ id: overlayStackId, type: 'popover' })

    return () => unregister(overlayStackId)
  }, [isOpen, overlayStackId, register, unregister])

  // Handle escape keydown
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === KEYBOARD_KEYS.ESCAPE && isTop(overlayStackId)) {
        close()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, isTop, overlayStackId, close])

  const cta = data?.cta
  const ctaParams = useMemo(() => (cta ? getCMSCtaLinkParams(cta) : null), [cta])
  const imageSource = data?.image?.asset?.asset?.url ? data.image.asset : null

  if (!isOpen || !data) return null

  return (
    <ClientOnlyPortal selector='#overlay-portal'>
      <div
        className={styles.popover}
        style={{ zIndex }}
        role='dialog'
        aria-modal='false'
        aria-labelledby={`${OVERLAY_ID}-description`}
        {...addTestId('marketing-popover')}
      >
        <Button
          className={styles.closeButton}
          variant={ButtonVariants.neutralText}
          aria-label='Zamknij'
          onClick={close}
          startIcon={<IconX size={20} />}
          {...addTestId('marketing-popover-close')}
        />

        {imageSource && (
          <Image
            className={styles.image}
            src={urlFor(imageSource).width(600).url()}
            alt={data.image?.alt || ''}
            width={600}
            height={200}
            loading='lazy'
            decoding='async'
          />
        )}

        <div className={styles.content}>
          {data.description && (
            <div
              className={styles.description}
              id={`${OVERLAY_ID}-description`}
            >
              <RichText value={data.description} />
            </div>
          )}

          {ctaParams?.href && (
            <LinkButton
              href={ctaParams.href}
              variant={cta?.design || ButtonVariants.primary}
              fullWidth
              onClick={close}
              {...addTestId('marketing-popover-cta')}
            >
              {ctaParams.text}
            </LinkButton>
          )}
        </div>
      </div>
    </ClientOnlyPortal>
  )
}

export default MarketingPopover
